/**
 * CLEARO currency switch — <clearo-currency>
 * Opens/closes the currency menu. In "display" mode (store sells in one
 * currency) it converts every price on the page in the browser, using
 * Shopify's daily exchange rates (cdn.shopify.com/s/javascripts/currencies.js),
 * and shows them with "≈". Checkout is still charged in the store currency.
 * In "market" mode the menu items submit Shopify's localization form instead.
 * Markup: snippets/clearo-currency-switch.liquid
 */
(() => {
  if (customElements.get('clearo-currency')) return;

  const RATES_URL = 'https://cdn.shopify.com/s/javascripts/currencies.js';
  const STORAGE_KEY = 'clr-currency';
  const ALIASES = { ILS: ['ILS', 'NIS', '₪'] };
  const SKIP = 'script, style, textarea, input, select, option, noscript, clearo-currency, [data-no-convert]';

  const store = {
    get() {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return null;
      }
    },
    set(v) {
      try {
        localStorage.setItem(STORAGE_KEY, v);
      } catch (e) {}
    },
  };

  let ratesPromise;
  const loadRates = () => {
    if (window.Currency && window.Currency.rates) return Promise.resolve(window.Currency.rates);
    ratesPromise =
      ratesPromise ||
      new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = RATES_URL;
        s.async = true;
        s.onload = () => (window.Currency && window.Currency.rates ? resolve(window.Currency.rates) : reject());
        s.onerror = reject;
        document.head.appendChild(s);
      });
    return ratesPromise;
  };

  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /** Converts store-currency amounts inside text nodes, and can restore them. */
  class PriceConverter {
    constructor(base) {
      this.base = base;
      const marks = (ALIASES[base] || [base]).map(escapeRe).join('|');
      const num = '(\\d{1,3}(?:,\\d{3})*(?:\\.\\d{2})?)';
      this.re = new RegExp(`(?:(?:${marks})\\s?${num})|(?:${num}\\s?(?:${marks}))`, 'g');
      this.seen = new WeakMap(); // text node -> { orig, shown }
      this.target = base;
      this.rates = null;
    }

    format(value) {
      const opts = { style: 'currency', currency: this.target, minimumFractionDigits: 2, maximumFractionDigits: 2 };
      // Isolate so the price reads correctly on right-to-left pages
      return `⁦≈${new Intl.NumberFormat('en', opts).format(value)}⁩`;
    }

    convertText(text) {
      const from = this.rates[this.base];
      const to = this.rates[this.target];
      return text.replace(this.re, (m, a, b) => {
        const amount = parseFloat((a || b).replace(/,/g, ''));
        return Number.isFinite(amount) ? this.format((amount * from) / to) : m;
      });
    }

    processNode(node) {
      const rec = this.seen.get(node);
      const orig = rec && node.nodeValue === rec.shown ? rec.orig : node.nodeValue;
      this.re.lastIndex = 0;
      if (!rec && !this.re.test(orig)) return;
      const shown = this.target === this.base || !this.rates ? orig : this.convertText(orig);
      if (node.nodeValue !== shown) node.nodeValue = shown;
      this.seen.set(node, { orig, shown });
    }

    run(root = document.body) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) =>
          n.parentElement && !n.parentElement.closest(SKIP) && n.nodeValue.trim()
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT,
      });
      let n;
      while ((n = walker.nextNode())) this.processNode(n);
    }

    watch() {
      if (this.observer) return;
      let queued = false;
      this.observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        setTimeout(() => {
          queued = false;
          this.run();
        }, 40);
      });
      this.observer.observe(document.body, { subtree: true, childList: true, characterData: true });
    }
  }

  // Every switch on the page (header + product page) shares one choice
  const instances = new Set();
  let converter = null;
  let applyRun = 0;

  async function applyCurrency(code, base) {
    converter = converter || new PriceConverter(base);
    instances.forEach((el) => el.showCurrency(code));
    store.set(code);
    const run = ++applyRun;
    converter.target = code;
    if (code !== base) {
      try {
        converter.rates = await loadRates();
      } catch (e) {
        // Rates unavailable: keep store prices rather than show wrong ones
        if (run === applyRun) applyCurrency(base, base);
        return;
      }
      if (run !== applyRun) return;
      if (!converter.rates[code] || !converter.rates[base]) converter.target = base;
    }
    converter.run();
    converter.watch();
  }

  class ClearoCurrency extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      this.base = this.dataset.base;
      this.toggle = this.querySelector('.clr-cur__toggle');
      this.menu = this.querySelector('.clr-cur__menu');
      this.items = [...this.querySelectorAll('.clr-cur__item')];

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.onDocClick = (e) => !this.contains(e.target) && this.close();

      if (this.mode !== 'display') return;

      instances.add(this);
      this.items.forEach((btn) => btn.addEventListener('click', () => this.choose(btn.dataset.currency)));

      const current = converter ? converter.target : store.get();
      if (!current || !this.items.some((b) => b.dataset.currency === current)) return;
      if (converter) this.showCurrency(current);
      else if (current !== this.base) applyCurrency(current, this.base);
    }

    disconnectedCallback() {
      instances.delete(this);
      document.removeEventListener('click', this.onDocClick);
    }

    get isOpen() {
      return !this.menu.hidden;
    }

    open() {
      this.menu.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.menu.getBoundingClientRect(); // start the open transition from the hidden state
      this.classList.add('is-open');
      document.addEventListener('click', this.onDocClick);
      const current = this.items.find((b) => b.getAttribute('aria-pressed') === 'true') || this.items[0];
      current && current.focus();
    }

    close(focusToggle) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocClick);
      this.menu.hidden = true;
      if (focusToggle) this.toggle.focus();
    }

    onKey(e) {
      if (e.key === 'Escape' && this.isOpen) {
        e.preventDefault();
        this.close(true);
        return;
      }
      if (!this.isOpen || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const i = this.items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % this.items.length : (i - 1 + this.items.length) % this.items.length;
      this.items[next].focus();
    }

    choose(code) {
      this.close(true);
      applyCurrency(code, this.base);
    }

    showCurrency(code) {
      const picked = this.items.find((b) => b.dataset.currency === code);
      if (!picked) return;
      this.items.forEach((b) => b.setAttribute('aria-pressed', String(b === picked)));
      this.querySelector('[data-code]').textContent = code;
      this.querySelector('[data-symbol]').textContent = picked.querySelector('.clr-cur__item-symbol').textContent.trim();
      this.classList.toggle('is-converted', code !== this.base);
    }
  }

  customElements.define('clearo-currency', ClearoCurrency);
})();
